import { giphyBlack, giphyBlue, giphyDarkestGrey, giphyDarkGrey, giphyGreen, giphyLightGrey } from '@giphy/js-brand'
import { Button, SelectList } from '@giphy/react-giphy-brand'
import { invert, keys, values } from 'lodash'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Group, Layer, Rect, Stage, Text } from 'react-konva'
import MessageContext from 'shared/components/context/message'
import TextArea from 'shared/components/inputs/text-area'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import styled, { css } from 'styled-components'
import ColorPicker from '../shared/components/color-picker'
import CropPreview from '../shared/components/crop-preview'
import RangeSlider from '../shared/components/range-slider'
import { ICapture, ISegment } from '../shared/types'
import { getFontColorOptions, getFontNameOptions, getFontSizeOptions } from '../shared/utils/utils'

const PREVIEW_VIDEO_WIDTH = 355
const CAPTION_RECT_PADDING = 20

const Container = styled.div`
    padding: 25px;
`

const Heading = styled.div`
    width: 100%;
    margin-bottom: 15px;
    border-bottom: 1px solid ${giphyDarkGrey};
`

const Tab = styled.div<{ active: boolean }>`
    font-family: nexablack;
    display: inline-block;
    margin-right: 25px;
    font-size: 16px;
    cursor: pointer;
    height: 35px;
    border-bottom: 4px solid ${props => (props.active ? giphyBlue : 'transparent')};
`

const Content = styled.div`
    padding-left: 12px;
    height: 310px;
`

const Preview = styled.div`
    width: 50%;
    float: left;
    position: relative;
`

const PreviewVideo = styled.video`
    width: ${PREVIEW_VIDEO_WIDTH}px;
`

const Fields = styled.div`
    padding-left: 12px;
    width: 50%;
    float: right;
`

const FieldLabel = styled.div`
    font-size: 14px;
    font-weight: bold;
    height: 16px;
    color: ${giphyLightGrey};
    margin-bottom: 5px;
    &:not(:first-child) {
        margin-top: 20px;
    }
`

const Actions = styled.div`
    margin: 100px auto 0 auto;
    width: 500px;
`

const SaveButton = styled(Button)`
    display: block;
    width: 100%;
    margin-bottom: 10px;
`

const CancelButton = styled(SaveButton)`
    background-color: ${giphyDarkestGrey};
`

const CropSelect = styled.div`
    display: grid;
    grid-template-columns: auto auto auto;
    grid-gap: 8px;
`

const CropItem = styled.div<{ active: boolean }>`
    padding: 10px;
    background-color: ${giphyBlack};
    cursor: pointer;
    display: table;
    height: 100%;

    ${props =>
        props.active &&
        css`
            background-color: ${giphyGreen};
            color: ${giphyBlack};
        `}
`
const CropLabel = styled.div`
    display: table-cell;
    font-weight: bold;
    text-align: center;
    vertical-align: middle;
`

const CaptionStage = styled(Stage)<{ width: number; height: number }>`
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: top left;
    transform: scale(${props => PREVIEW_VIDEO_WIDTH / props.width});
`

type Props = {
    capture: ICapture
    segment: ISegment
    onCancel?: () => void
    onSave?: (segment: ISegment) => void
    soundEnabled?: boolean
}

const Editor = ({ capture, segment, onCancel = () => {}, onSave = () => {}, soundEnabled = false }: Props) => {
    const FONT_NAME_OPTIONS = getFontNameOptions()
    const FONT_SIZE_OPTIONS = getFontSizeOptions(capture)
    const FONT_COLOR_OPTIONS = getFontColorOptions()

    const { sendMessage } = useContext(MessageContext)

    const captionStageRef = useRef<HTMLCanvasElement | null>(null)
    const captionGroupRef = useRef(null)
    const captionTextRef = useRef(null)
    const captionRectRef = useRef(null)
    const videoRef = useRef<HTMLVideoElement | null>(null)

    const [activeTab, setActiveTab] = useState('edit')
    const [captionFontSize, setCaptionFontSize] = useState<number>()
    const [captionFontName, setCaptionFontName] = useState<string>()
    const [captionFontColor, setCaptionFontColor] = useState<string>()
    const [captionText, setCaptionText] = useState('')
    const [captionX, setCaptionX] = useState(0)
    const [captionY, setCaptionY] = useState(0)
    const [captionTextWidth, setCaptionTextWidth] = useState(0)
    const [captionTextHeight, setCaptionTextHeight] = useState(0)

    const [startTime, setStartTime] = useState(0)
    const [endTime, setEndTime] = useState(segment.duration)

    const [aspectRatio, setAspectRatio] = useState('original')

    const [isLoading, setIsLoading] = useState(true)

    let timeoutId: null | ReturnType<typeof setTimeout> = null

    useEffect(() => {
        setCaptionText(segment.editParams.captionText)
        setCaptionX(segment.editParams.captionX)
        setCaptionY(segment.editParams.captionY)
        setCaptionFontName(segment.editParams.captionFontName)
        setCaptionFontSize(segment.editParams.captionFontSize)
        setCaptionFontColor(segment.editParams.captionFontColor)
        setStartTime(segment.editParams.trimmedStartTime)
        setEndTime(segment.editParams.trimmedEndTime)
        setAspectRatio(segment.editParams.aspectRatio)

        setIsLoading(false)
    }, [])

    useEffect(() => {
        // When caption or font properties change we need to recalculate the text width
        const { width, height } = calcCaptionTextSize()
        setCaptionTextWidth(width)
        setCaptionTextHeight(height)
    }, [captionText, captionFontSize, captionFontName])

    const fireEditEvents = ({
        duration,
        editParams: { captionText, aspectRatio, captionFontName, trimmedStartTime, trimmedEndTime },
    }: ISegment) => {
        if (aspectRatio !== 'original') {
            googleAnalyticsEvent({
                category: 'Super Cut',
                action: 'Edit-Crop',
                label: aspectRatio,
            })
        }
        if (captionText !== '') {
            googleAnalyticsEvent({
                category: 'Super Cut',
                action: 'Edit-Add Caption',
                label: captionFontName,
            })
        }
        if (duration !== trimmedEndTime || trimmedStartTime !== 0) {
            googleAnalyticsEvent({
                category: 'Super Cut',
                action: 'Edit-Trim',
                label: capture.id,
            })
        }
    }

    const saveClicked = () => {
        const captionStageNode = captionStageRef.current as HTMLCanvasElement
        const captionGroupNode = captionGroupRef.current as any
        const captionRectNode = captionRectRef.current as any

        captionRectNode.hide()
        const captionWatermark = captionStageNode.toDataURL()
        captionRectNode.show()

        const captionX = captionGroupNode.x()
        const captionY = captionGroupNode.y()

        const modifiedSegment = {
            ...segment,
            editParams: {
                ...segment.editParams,
                captionX: captionX,
                captionY: captionY,
                captionText: captionText,
                captionWatermark: captionWatermark,
                captionFontSize: captionFontSize,
                captionFontColor: captionFontColor,
                captionFontName: captionFontName,
                trimmedStartTime: startTime,
                trimmedEndTime: endTime,
                aspectRatio: aspectRatio,
            },
        } as ISegment
        fireEditEvents(modifiedSegment)
        sendMessage('GIF Saved!')
        onSave(modifiedSegment)
    }

    const cancelClicked = () => {
        onCancel()
    }

    const captionTextChanged = (value: string) => {
        setCaptionText(value)
    }

    const onAspectRatioChanged = aspectRatio => {
        setAspectRatio(aspectRatio)
    }

    const beginTimeout = () => {
        timeoutId && clearTimeout(timeoutId)

        const id = setTimeout(() => {
            const video = videoRef.current!
            video.play()
        }, 800)

        // @ts-ignore
        timeoutId = id
    }

    const startTimeChanged = (value: number) => {
        const video = videoRef.current!
        video.pause()
        video.currentTime = value
        setStartTime(value)
        beginTimeout()
    }

    const endTimeChanged = (value: number) => {
        const video = videoRef.current!
        video.pause()
        video.currentTime = value
        setEndTime(value)
        beginTimeout()
    }

    const videoTimeUpdated = () => {
        const video = videoRef.current!

        if (video.currentTime >= endTime) {
            video.currentTime = startTime
            video.play()
        }
    }

    const calcCaptionTextSize = () => {
        const captionTextNode = captionTextRef.current as any
        if (captionTextNode) {
            return captionTextNode.measureSize(captionText)
        } else {
            return { width: 0, height: 0 }
        }
    }

    if (isLoading) {
        return null
    } else {
        // TODO: Replace color-picker
        return (
            <Container>
                <Heading>
                    <Tab active={activeTab === 'edit'} onClick={() => setActiveTab('edit')}>
                        Edit GIF
                    </Tab>
                    <Tab active={activeTab === 'caption'} onClick={() => setActiveTab('caption')}>
                        Add Caption
                    </Tab>
                </Heading>
                <Content>
                    <Preview>
                        <PreviewVideo
                            ref={videoRef}
                            src={segment.video_url}
                            autoPlay={true}
                            muted={!soundEnabled}
                            onTimeUpdate={videoTimeUpdated}
                        />
                        <CaptionStage ref={captionStageRef} width={capture.width} height={capture.height}>
                            <Layer>
                                <Group ref={captionGroupRef} draggable={true} x={captionX} y={captionY}>
                                    <Text
                                        ref={captionTextRef}
                                        text={captionText}
                                        fill={captionFontColor}
                                        fontSize={captionFontSize}
                                        fontFamily={captionFontName}
                                        align={'center'}
                                        verticalAlign={'middle'}
                                        padding={CAPTION_RECT_PADDING}
                                    />
                                    <Rect
                                        ref={captionRectRef}
                                        stroke={'white'}
                                        strokeWidth={8}
                                        dash={[15, 8]}
                                        width={captionTextWidth + CAPTION_RECT_PADDING * 2}
                                        height={captionTextHeight + CAPTION_RECT_PADDING * 2}
                                        visible={activeTab === 'caption'}
                                    />
                                </Group>
                            </Layer>
                        </CaptionStage>
                        <CropPreview
                            originalWidth={capture.width}
                            originalHeight={capture.height}
                            targetAspectRatio={aspectRatio}
                            renderedWidth={PREVIEW_VIDEO_WIDTH}
                        />
                    </Preview>

                    {activeTab === 'edit' ? (
                        <Fields>
                            <FieldLabel>Crop</FieldLabel>
                            <CropSelect>
                                <CropItem
                                    active={aspectRatio === 'original'}
                                    onClick={() => onAspectRatioChanged('original')}
                                >
                                    <CropLabel>Original</CropLabel>
                                </CropItem>
                                <CropItem active={aspectRatio === '1:1'} onClick={() => onAspectRatioChanged('1:1')}>
                                    <CropLabel>1:1</CropLabel>
                                </CropItem>
                                <CropItem active={aspectRatio === '3:2'} onClick={() => onAspectRatioChanged('3:2')}>
                                    <CropLabel>3:2</CropLabel>
                                </CropItem>
                                <CropItem active={aspectRatio === '16:9'} onClick={() => onAspectRatioChanged('16:9')}>
                                    <CropLabel>16:9</CropLabel>
                                </CropItem>
                                <CropItem active={aspectRatio === '4:5'} onClick={() => onAspectRatioChanged('4:5')}>
                                    <CropLabel>4:5</CropLabel>
                                </CropItem>
                                <CropItem active={aspectRatio === '9:16'} onClick={() => onAspectRatioChanged('9:16')}>
                                    <CropLabel>9:16</CropLabel>
                                </CropItem>
                            </CropSelect>
                            <FieldLabel>Trim GIF</FieldLabel>
                            <RangeSlider
                                min={0}
                                max={segment.duration}
                                value1={startTime}
                                value2={endTime}
                                label1={`${(endTime - startTime).toFixed(1)}s`}
                                label2={`${(endTime - startTime).toFixed(1)}s`}
                                minDiff={1}
                                onValue1Changed={startTimeChanged}
                                onValue2Changed={endTimeChanged}
                            />
                        </Fields>
                    ) : (
                        <Fields>
                            <FieldLabel>Caption</FieldLabel>
                            <TextArea value={captionText} onChange={captionTextChanged} />

                            <FieldLabel>{`Font & Style`}</FieldLabel>
                            <SelectList
                                theme={'dark'}
                                options={keys(FONT_NAME_OPTIONS)}
                                active={[invert(FONT_NAME_OPTIONS)[captionFontName!]]}
                                placeholder={'Font'}
                                allowMultiple={false}
                                onChange={v => setCaptionFontName(FONT_NAME_OPTIONS[v])}
                            />

                            <FieldLabel>Font Size</FieldLabel>
                            <SelectList
                                theme={'dark'}
                                options={keys(FONT_SIZE_OPTIONS)}
                                active={[invert(FONT_SIZE_OPTIONS)[captionFontSize!]]}
                                placeholder={'Font Size'}
                                allowMultiple={false}
                                onChange={v => setCaptionFontSize(FONT_SIZE_OPTIONS[v])}
                            />

                            <FieldLabel>Color</FieldLabel>
                            <ColorPicker
                                colors={values(FONT_COLOR_OPTIONS)}
                                selectedColor={captionFontColor}
                                onChange={v => setCaptionFontColor(v)}
                            />
                        </Fields>
                    )}
                </Content>
                <Actions>
                    <SaveButton onClick={saveClicked}>Save</SaveButton>
                    <CancelButton onClick={cancelClicked}>Cancel</CancelButton>
                </Actions>
            </Container>
        )
    }
}

export default Editor
